import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getRandomContent, recordProgress, awardBonus, Content } from '../services/api';
import { speak } from '../services/audio';
import './Games.css';

const DictationGame: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const lessonId = searchParams.get('lessonId') || undefined;
  const customSetId = searchParams.get('customSetId') || undefined;
  const inputRef = useRef<HTMLInputElement>(null);

  const [items, setItems] = useState<Content[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [score, setScore] = useState({ correct: 0, total: 0 });
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playCount, setPlayCount] = useState(0);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const data = await getRandomContent(10, lessonId, customSetId);
        setItems(data);
      } catch (err) {
        console.error('Failed to load content:', err);
      } finally {
        setLoading(false);
      }
    };
    loadItems();
  }, [lessonId, customSetId]);

  useEffect(() => {
    if (!loading && items.length > 0) {
      setPlayCount(0);
    }
  }, [loading, currentIndex, items.length]);

  const handlePlay = () => {
    if (items.length > 0) {
      speak(items[currentIndex].english);
      setPlayCount(prev => prev + 1);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  };

  const handlePlaySlow = () => {
    if (items.length > 0) {
      // Speak slower by using utterance rate
      const utterance = new SpeechSynthesisUtterance(items[currentIndex].english);
      utterance.lang = 'en-US';
      utterance.rate = 0.6;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
      setPlayCount(prev => prev + 1);
    }
  };

  const normalizeAnswer = (text: string): string => {
    return text.toLowerCase().trim().replace(/[.,!?]/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (feedback) return;

    const currentItem = items[currentIndex];
    const isCorrect = normalizeAnswer(userInput) === normalizeAnswer(currentItem.english);

    setFeedback(isCorrect ? 'correct' : 'wrong');
    await recordProgress(currentItem.id, isCorrect);

    setScore(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      total: prev.total + 1
    }));
  };

  const handleNext = () => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setUserInput('');
      setFeedback(null);
    } else {
      handleComplete();
    }
  };

  const handleComplete = async () => {
    if (score.correct + (feedback === 'correct' ? 1 : 0) === items.length) {
      await awardBonus(30, 'perfect_dictation');
    }
    setCompleted(true);
  };

  if (loading) return <div className="loading">Ładowanie...</div>;
  if (items.length < 4) {
    return (
      <div className="game-error">
        <h2>Za mało słówek!</h2>
        <p>Potrzebujesz minimum 4 słówek do tej gry.</p>
        <button className="btn btn-primary" onClick={() => navigate('/games')}>Wróć</button>
      </div>
    );
  }

  const currentItem = items[currentIndex];
  if (!currentItem) return <div className="loading">Ładowanie...</div>;

  if (completed) {
    const isPerfect = score.correct === items.length;
    return (
      <div className="game-complete fade-in">
        <div className="complete-icon">{isPerfect ? '🏆' : '🎉'}</div>
        <h1>{isPerfect ? 'Perfekcyjnie!' : 'Świetnie!'}</h1>
        <p>Wynik: {score.correct} / {score.total}</p>
        {isPerfect && <div className="bonus-badge bounce">+30 bonus!</div>}
        <button className="btn btn-primary btn-large" onClick={() => window.location.reload()}>
          Zagraj ponownie
        </button>
      </div>
    );
  }

  return (
    <div className="game-container fade-in">
      <div className="game-header">
        <h2>👂 Dyktando</h2>
        <div className="game-progress">{currentIndex + 1} / {items.length}</div>
      </div>

      <div className="game-score">✅ {score.correct} / {score.total}</div>

      <div className="dictation-card">
        <p className="quiz-instruction">Posłuchaj i napisz co słyszysz:</p>
        
        <div className="dictation-controls">
          <button className="btn btn-listen" onClick={handlePlay} disabled={!!feedback}>
            🔊 Posłuchaj
          </button>
          <button className="btn btn-listen-slow" onClick={handlePlaySlow} disabled={!!feedback}>
            🐢 Wolniej
          </button>
        </div>

        {playCount >= 3 && (
          <div className="hint-text">
            Podpowiedź: {currentItem.polish}
          </div>
        )}

        <form onSubmit={handleSubmit} className="dictation-form">
          <input
            ref={inputRef}
            type="text"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            className={`spelling-input ${feedback || ''}`}
            placeholder="Wpisz co słyszysz..."
            disabled={!!feedback}
            autoComplete="off"
            autoCapitalize="off"
          />
          <button 
            type="submit" 
            className="btn btn-primary" 
            disabled={!userInput.trim() || !!feedback || playCount === 0}
          >
            Sprawdź ➜
          </button>
        </form>

        {feedback === 'wrong' && (
          <div className="correct-answer">
            Poprawna odpowiedź: <strong>{currentItem.english}</strong>
            <button className="speak-btn-inline" onClick={() => speak(currentItem.english)}>🔊</button>
          </div>
        )}

        {feedback === 'correct' && (
          <div className="correct-feedback">
            ✅ Dobrze! <strong>{currentItem.english}</strong>
          </div>
        )}

        {feedback && (
          <button className="btn btn-primary btn-next" onClick={handleNext}>
            Dalej ➜
          </button>
        )}
      </div>
    </div>
  );
};

export default DictationGame;

